// Akebono WYSIWYG エディタ (Lexical ベース、Notion 風ミニマル UI)
//
// ツールバー設計:
//   [段落 ▾]  │  B  I  S  </>  🔗  │  [+ 挿入 ▾]  │  ↶  ↷
//
//   - 「段落 ▾」: ブロック種別ドロップダウン (¶/H₂/H₃/H₄/箇条書き/番号付き/引用/コード)
//   - B I S </>: 最頻出のインライン装飾 (toggle)
//   - 🔗 : リンク
//   - 「+ 挿入 ▾」: テーブル/Callout/Macro/HR
//   - ↶ ↷ : Undo/Redo
//   それ以外は Bubble menu (選択時) と Slash menu ("/") で呼び出す。

import { Controller } from "@hotwired/stimulus"
import {
  createEditor,
  $getRoot, $getSelection, $isRangeSelection, $createParagraphNode,
  FORMAT_TEXT_COMMAND, UNDO_COMMAND, REDO_COMMAND,
  COMMAND_PRIORITY_EDITOR,
} from "lexical"
import {
  HeadingNode, QuoteNode,
  $createHeadingNode, $createQuoteNode, $isHeadingNode, $isQuoteNode,
  registerRichText,
} from "@lexical/rich-text"
import {
  ListNode, ListItemNode, $isListNode, $isListItemNode,
  registerList,
} from "@lexical/list"
import { CodeNode, CodeHighlightNode, $isCodeNode, registerCodeHighlighting } from "@lexical/code"
import { LinkNode, $isLinkNode, $toggleLink, TOGGLE_LINK_COMMAND } from "@lexical/link"
import {
  TableNode, TableRowNode, TableCellNode,
  registerTablePlugin, registerTableSelectionObserver,
} from "@lexical/table"
import { $setBlocksType } from "@lexical/selection"
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html"
import { mergeRegister } from "@lexical/utils"
import { registerHistory, createEmptyHistoryState } from "@lexical/history"
import { registerMarkdownShortcuts, TRANSFORMERS } from "@lexical/markdown"
import tippy from "tippy.js"

import { CalloutNode } from "./editor/callout_node"
import { PageLinkNode } from "./editor/page_link_node"
import { MacroNode } from "./editor/macro_node"
import { ImageNode, $createImageNode } from "./editor/image_node"
import { AnchorNode, $createAnchorNode } from "./editor/anchor_node"
import { DrawioNode, $createDrawioNode } from "./editor/drawio_node"
import { openDrawioEditor } from "./editor/drawio_editor"
import { attachDrawioEdit } from "./editor/drawio_edit"
import { uploadFile } from "./editor/image_upload"
import { $getNodeByKey } from "lexical"
import { attachSlashMenu } from "./editor/slash_menu"
import { attachBubbleMenu } from "./editor/bubble_menu"
import { attachLinkPopover } from "./editor/link_popover"
import { BLOCK_TYPE_GROUPS, INSERT_GROUPS } from "./editor/block_commands"
import { attachTableFloatingMenu } from "./editor/table_floating_menu"
import { $getTableCellNodeFromLexicalNode, $isTableCellNode } from "@lexical/table"
import { uploadImage, isImageFile } from "./editor/image_upload"
import { attachImageResize } from "./editor/image_resize"
import { LocalDraft, relativeTime, pruneStaleDrafts } from "./editor/local_draft"
import { $insertNodeToNearestRoot } from "@lexical/utils"

import {
  Bold, Italic, Strikethrough, Code,
  Link as LinkIcon, Plus, Undo2, Redo2, ChevronDown,
  Image as ImageIcon,
  History as HistoryIcon,
  X as XIcon,
  createElement,
} from "lucide"

const isMac = typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgentData?.platform || "")
const Mod = isMac ? "⌘" : "Ctrl"

function lucideSvg(IconNode, className = "w-4 h-4") {
  const el = createElement(IconNode)
  el.setAttribute("class", className)
  el.setAttribute("stroke-width", "2")
  return el.outerHTML
}

export default class extends Controller {
  static targets = [
    "surface", "textarea", "toolbar", "bubble",
    // ローカルドラフト用
    "draftBanner", "titleInput", "tagsInput", "editMessageInput",
    // 差分プレビュー用
    "diffModal", "diffBody",
  ]

  static values = { drawioUrl: String }

  connect() {
    this.editor = createEditor({
      namespace: "akebono",
      nodes: [
        HeadingNode, QuoteNode,
        ListNode, ListItemNode,
        CodeNode, CodeHighlightNode, LinkNode,
        TableNode, TableRowNode, TableCellNode,
        CalloutNode, PageLinkNode, MacroNode, ImageNode, DrawioNode, AnchorNode,
      ],
      theme: {
        paragraph: "", heading: { h1: "", h2: "", h3: "", h4: "", h5: "" },
        list: { ul: "", ol: "", listitem: "" }, quote: "", code: "", link: "",
        text: { bold: "font-bold", italic: "italic", underline: "underline",
                strikethrough: "line-through", code: "akebono-inline-code" },
        table:                          "akebono-table",
        tableRow:                       "akebono-tr",
        tableCell:                      "akebono-td",
        tableCellHeader:                "akebono-th",
        tableCellSelected:              "akebono-cell-selected",
        tableSelected:                  "akebono-table-selected",
        tableSelection:                 "akebono-table-selection",
        tableCellActionButton:          "akebono-cell-action-btn",
        tableCellActionButtonContainer: "akebono-cell-action-container",
        tableCellResizer:               "akebono-cell-resizer",
      },
      onError: (error) => console.error("[Lexical]", error),
    })

    // surface を編集可能に + mount
    this.surfaceTarget.setAttribute("contenteditable", "true")
    this.surfaceTarget.setAttribute("role", "textbox")
    this.surfaceTarget.setAttribute("aria-multiline", "true")
    this.surfaceTarget.classList.add("akebono-prose", "ProseMirror", "min-h-[400px]", "p-6")
    this.surfaceTarget.style.outline = "none"
    this.editor.setRootElement(this.surfaceTarget)

    // 初期 HTML を import (register より前)
    const initial = this.textareaTarget.value || "<p></p>"
    this.editor.update(() => {
      const root = $getRoot()
      root.clear()
      const dom = new DOMParser().parseFromString(initial, "text/html")
      const nodes = $generateNodesFromDOM(this.editor, dom)
      if (nodes.length === 0) {
        root.append($createParagraphNode())
      } else {
        const wrapped = nodes.map(n =>
          n.isInline?.() ? $createParagraphNode().append(n) : n
        )
        root.append(...wrapped)
      }
    }, { discrete: true })

    // プラグイン handler 全 register
    this.cleanupPlugins = mergeRegister(
      registerRichText(this.editor),
      registerList(this.editor),
      this.editor.registerCommand(TOGGLE_LINK_COMMAND, (payload) => {
        if (payload === null) $toggleLink(null)
        else if (typeof payload === "string") $toggleLink(payload)
        else { const { url, target, rel, title } = payload; $toggleLink(url, { rel, target, title }) }
        return true
      }, COMMAND_PRIORITY_EDITOR),
      registerTablePlugin(this.editor),
      registerTableSelectionObserver(this.editor),
      registerCodeHighlighting(this.editor),
      registerHistory(this.editor, createEmptyHistoryState(), 300),
      // Markdown 入力ショートカット:
      //   "- "/"* " → 箇条書き, "1. " → 番号付き, "# "/"## " → 見出し,
      //   "> " → 引用, "```" → コードブロック, "**bold**"/"*it*"/"`code`" → inline
      registerMarkdownShortcuts(this.editor, TRANSFORMERS),
    )

    // 編集ごとに HTML を textarea に書き戻し + toolbar 更新
    this.cleanupListener = this.editor.registerUpdateListener(() => {
      this.editor.read(() => {
        this.textareaTarget.value = $generateHtmlFromNodes(this.editor, null)
      })
      this.refreshToolbarState()
    })

    // 画像アップロード handler を editor インスタンスに attach
    // (slash menu / block_commands から `editor.akebonoUploadImage(file)` で呼べる)
    this.editor.akebonoUploadImage = this.uploadAndInsertImage.bind(this)
    // drawio 図: 編集 (既存ノード) / 新規挿入
    this.editor.akebonoEditDrawio = this.editDrawio.bind(this)
    this.editor.akebonoInsertDrawio = this.insertDrawio.bind(this)
    this.editor.akebonoInsertAnchor = this.insertAnchor.bind(this)

    // Slash / Bubble menu attach
    this.cleanupSlash = attachSlashMenu(this.editor)
    if (this.hasBubbleTarget) {
      this.cleanupBubble = attachBubbleMenu(this.editor, this.bubbleTarget)
    }

    // 一度 textarea に初期 HTML を反映
    this.editor.read(() => {
      this.textareaTarget.value = $generateHtmlFromNodes(this.editor, null)
    })

    // 画像アップロード経路: ドラッグ&ドロップ / クリップボードペースト
    this.attachImageUploadHandlers()

    // 画像のクリック選択 + ハンドルドラッグでリサイズ
    this.cleanupImageResize = attachImageResize(this.editor, this.surfaceTarget)

    // drawio 図の「編集」ボタン委譲
    this.cleanupDrawioEdit = attachDrawioEdit(this.editor, this.surfaceTarget)

    // リンク編集ポップオーバー (リンク内フォーカス時: 移動/編集/解除)
    this.cleanupLinkPopover = attachLinkPopover(this.editor, this.surfaceTarget)

    // テーブル: カーソルがセル内のとき、 セル付近に floating action menu を出す
    this.cleanupTableMenu = attachTableFloatingMenu(this.editor, this.surfaceTarget)

    // ローカルドラフト (localStorage) を初期化
    this.setupLocalDraft()

    // form submit で draft を即クリア (success/fail にかかわらず一旦消す)
    // - 成功時: server 反映済み、 draft は不要
    // - 失敗時: validation エラーで edit に戻ってくるが、 form input は再描画されるので
    //   ユーザーが見える状態は維持される。 万一消えても次の入力で再保存される。
    this._onFormSubmit = () => {
      this.draft?.flush?.({
        // flush 不要、 削除する
      })
      this.draft?.clear?.()
    }
    this.element.addEventListener("submit", this._onFormSubmit)

    this.renderToolbar()
    this.refreshToolbarState()
  }

  disconnect() {
    this.cleanupSlash?.()
    this.cleanupBubble?.()
    this.cleanupListener?.()
    this.cleanupPlugins?.()
    this.cleanupImageDnd?.()
    this.cleanupImageResize?.()
    this.cleanupTableMenu?.()
    this.cleanupDrawioEdit?.()
    this.cleanupLinkPopover?.()
    if (this._onFormSubmit) this.element.removeEventListener("submit", this._onFormSubmit)
    this.dropdownPopups?.forEach(p => p.destroy())
  }

  // ─── ローカルドラフト ──────────────────────────────

  setupLocalDraft() {
    if (!this.hasDraftBannerTarget) return  // edit/new フォーム以外なら何もしない
    const banner = this.draftBannerTarget
    const key    = banner.dataset.editorDraftKey
    const baseV  = parseInt(banner.dataset.editorBaseVersion || "0", 10)
    if (!key) return

    // 古い draft (古い base_version) を一掃
    if (baseV > 0) pruneStaleDrafts({ pageId: this._pageIdFromKey(key), currentBaseVersion: baseV })

    this.draft = new LocalDraft({ key, debounceMs: 1000 })
    this._draftBaseVersion = baseV

    // DB 版を保持しておく (= 「変更なし」 の基準。 戻すボタンでも使う)
    this._dbSnapshot = this._snapshotState()

    // 既存 draft があり、 現在の DB 内容と diff があれば → 自動復元 + 通知 banner
    const saved = this.draft.load()
    if (saved && this._isDirty(this._draftToState(saved))) {
      this._restoreDraft(saved)
      this._showRestoredBanner(saved)
    }

    // editor の更新 / 入力ごとに、 dirty なら save、 cleanなら clear
    this._draftSaver = () => this._syncDraftWithState()
    this._origRefreshToolbarState = this.refreshToolbarState.bind(this)
    this.refreshToolbarState = () => {
      this._origRefreshToolbarState()
      this._draftSaver()
    }
    ;["titleInput", "tagsInput", "editMessageInput"].forEach(t => {
      const target = this[`${t}Target`]
      if (!target) return
      const ev = () => this._draftSaver()
      target.addEventListener("input", ev)
      this[`_${t}Listener`] = ev
    })
  }

  // 現在のフォーム状態をスナップショット
  _snapshotState() {
    return {
      title:        this.hasTitleInputTarget       ? this.titleInputTarget.value       : "",
      tags:         this.hasTagsInputTarget        ? this.tagsInputTarget.value        : "",
      edit_message: this.hasEditMessageInputTarget ? this.editMessageInputTarget.value : "",
      html:         this.textareaTarget?.value     || "",
    }
  }

  // localStorage の payload 形式から本体 state を取り出す
  _draftToState(saved) {
    return {
      title:        saved?.title        || "",
      tags:         saved?.tags         || "",
      edit_message: saved?.edit_message || "",
      html:         saved?.html         || "",
    }
  }

  // DB 版 (_dbSnapshot) との差分判定。 edit_message は揮発 (draft 専用) なので
  // 比較対象外。 これがコアロジック: dirty なときだけ draft を持つ。
  _isDirty(state = this._snapshotState()) {
    const db = this._dbSnapshot
    if (!db) return false
    return state.title !== db.title ||
           state.tags  !== db.tags  ||
           state.html  !== db.html
  }

  // フォーム状態に合わせて draft を save / clear
  _syncDraftWithState() {
    if (!this.draft) return
    const state = this._snapshotState()
    if (this._isDirty(state)) {
      this.draft.save({ ...state, base_version: this._draftBaseVersion })
    } else {
      // DB 版に戻ったので localStorage の draft を削除 (clean state)
      this.draft.clear()
    }
  }

  _pageIdFromKey(key) {
    const m = /:page:(\d+)/.exec(key)
    return m ? parseInt(m[1], 10) : null
  }

  _showRestoredBanner(saved) {
    const banner = this.draftBannerTarget
    banner.className = "mb-4 flex items-center gap-3 px-4 py-3 bg-akebono-primary-soft border border-indigo-200 text-akebono-primary-active rounded-lg text-sm"
    banner.innerHTML = `
      <span class="shrink-0">
        ${lucideSvg(HistoryIcon, "w-4 h-4")}
      </span>
      <div class="flex-1 min-w-0">
        <strong class="font-semibold">未保存の下書きから復元しました</strong>
        <span class="opacity-80 ml-1">${relativeTime(saved.saved_at)} に自動保存された内容です</span>
      </div>
      <button type="button" data-draft-action="revert"
        class="akebono-btn akebono-btn-secondary akebono-btn-sm">保存済みに戻す</button>
      <button type="button" data-draft-action="dismiss"
        class="akebono-btn akebono-btn-ghost akebono-btn-icon shrink-0" title="閉じる">
        ${lucideSvg(XIcon, "w-4 h-4")}
      </button>
    `
    banner.classList.remove("hidden")

    banner.querySelector('[data-draft-action="revert"]').addEventListener("click", (e) => {
      e.preventDefault()
      // DB 版に戻す: draft をクリアして、 入力 / editor を初期スナップショットに復元
      this.draft.clear()
      this._restoreDraft(this._dbSnapshot)
      banner.remove()
    })
    banner.querySelector('[data-draft-action="dismiss"]').addEventListener("click", (e) => {
      e.preventDefault()
      // banner だけ閉じる (draft はそのまま、 復元状態を維持)
      banner.remove()
    })
  }

  _restoreDraft(saved) {
    if (this.hasTitleInputTarget && saved.title != null) this.titleInputTarget.value = saved.title
    if (this.hasTagsInputTarget  && saved.tags  != null) this.tagsInputTarget.value  = saved.tags
    if (this.hasEditMessageInputTarget && saved.edit_message != null) {
      this.editMessageInputTarget.value = saved.edit_message
    }
    // editor 本文の差し替え
    if (saved.html != null) {
      this.editor.update(() => {
        const root = $getRoot()
        root.clear()
        const dom = new DOMParser().parseFromString(saved.html || "<p></p>", "text/html")
        const nodes = $generateNodesFromDOM(this.editor, dom)
        if (nodes.length === 0) {
          root.append($createParagraphNode())
        } else {
          const wrapped = nodes.map(n => n.isInline?.() ? $createParagraphNode().append(n) : n)
          root.append(...wrapped)
        }
      })
    }
  }

  // ─── 画像アップロード ────────────────────────────────

  // Space slug / Page id を URL から推測してアップロード先 URL を決定
  uploadUrl() {
    const m = window.location.pathname.match(/^\/spaces\/([^/]+)(?:\/pages\/(\d+))?/)
    if (!m) return null
    const slug   = m[1]
    const pageId = m[2]
    return pageId
      ? `/spaces/${slug}/pages/${pageId}/attachments`
      : `/spaces/${slug}/attachments`
  }

  async uploadAndInsertImage(file) {
    const url = this.uploadUrl()
    if (!url) {
      console.warn("[editor] cannot determine upload URL — outside a space context")
      return
    }
    try {
      const result = await uploadImage(file, url)
      this.editor.update(() => {
        const node = $createImageNode(result.url, file.name || "", null)
        $insertNodeToNearestRoot(node)
      })
    } catch (err) {
      console.error("[editor] image upload failed", err)
      window.alert(`画像のアップロードに失敗しました: ${err.message}`)
    }
  }

  // ─── drawio 図 ──────────────────────────────────────

  drawioEmbedUrl() {
    return this.hasDrawioUrlValue && this.drawioUrlValue
      ? this.drawioUrlValue
      : "https://embed.diagrams.net"
  }

  // 保存された xml/png を添付としてアップロードし {src, sourceUrl} を返す
  async saveDrawioAttachments({ xml, pngBlob }) {
    const url = this.uploadUrl()
    if (!url) throw new Error("アップロード先を特定できません (スペース外)")
    const stamp = Date.now()
    const xmlFile = new File([xml || ""], `diagram-${stamp}.drawio`, { type: "application/xml" })
    const srcRes = await uploadFile(xmlFile, url)
    let src = ""
    if (pngBlob) {
      const pngFile = new File([pngBlob], `diagram-${stamp}.png`, { type: "image/png" })
      src = (await uploadFile(pngFile, url)).url
    }
    return { src, sourceUrl: srcRes.url }
  }

  // 既存 DrawioNode を編集
  async editDrawio(nodeKey) {
    let sourceUrl = ""
    this.editor.getEditorState().read(() => {
      const node = $getNodeByKey(nodeKey)
      if (node && typeof node.getSourceUrl === "function") sourceUrl = node.getSourceUrl()
    })

    let xml = ""
    if (sourceUrl) {
      try { xml = await (await fetch(sourceUrl, { credentials: "same-origin" })).text() }
      catch (e) { console.warn("[editor] drawio source fetch failed", e) }
    }

    openDrawioEditor({
      xml, embedUrl: this.drawioEmbedUrl(),
      onSave: async (payload) => {
        try {
          const { src, sourceUrl: newSource } = await this.saveDrawioAttachments(payload)
          this.editor.update(() => {
            const node = $getNodeByKey(nodeKey)
            if (node && typeof node.setDiagram === "function") node.setDiagram(src, newSource)
          })
        } catch (err) {
          console.error("[editor] drawio save failed", err)
          window.alert(`図の保存に失敗しました: ${err.message}`)
        }
      },
    })
  }

  // 新規 drawio 図を作成して挿入
  insertDrawio() {
    if (!this.uploadUrl()) {
      window.alert("図の挿入はページ編集画面でのみ利用できます")
      return
    }
    openDrawioEditor({
      xml: "", embedUrl: this.drawioEmbedUrl(),
      onSave: async (payload) => {
        try {
          const { src, sourceUrl } = await this.saveDrawioAttachments(payload)
          this.editor.update(() => {
            $insertNodeToNearestRoot($createDrawioNode(src, sourceUrl))
          })
        } catch (err) {
          console.error("[editor] drawio insert failed", err)
          window.alert(`図の作成に失敗しました: ${err.message}`)
        }
      },
    })
  }

  // ─── 名前付きアンカー ───────────────────────────────

  insertAnchor() {
    const name = (window.prompt("アンカー名 (英数字・記号、空白不可)", "") || "").trim().replace(/\s+/g, "-")
    if (!name) return
    this.editor.update(() => {
      const sel = $getSelection()
      if ($isRangeSelection(sel)) {
        sel.insertNodes([$createAnchorNode(name)])
      }
    })
  }

  // file picker から1ファイル選択
  pickAndInsertImage() {
    const input = document.createElement("input")
    input.type   = "file"
    input.accept = "image/*"
    input.style.display = "none"
    input.addEventListener("change", () => {
      const f = input.files?.[0]
      if (f && isImageFile(f)) this.uploadAndInsertImage(f)
      input.remove()
    })
    document.body.appendChild(input)
    input.click()
  }

  attachImageUploadHandlers() {
    const surface = this.surfaceTarget

    // 1. ドラッグ&ドロップ
    const onDrop = (e) => {
      const files = Array.from(e.dataTransfer?.files || [])
      const images = files.filter(isImageFile)
      if (images.length === 0) return
      e.preventDefault()
      images.forEach(f => this.uploadAndInsertImage(f))
    }
    const onDragOver = (e) => {
      if (Array.from(e.dataTransfer?.items || []).some(i => i.kind === "file")) {
        e.preventDefault()
      }
    }
    surface.addEventListener("drop", onDrop)
    surface.addEventListener("dragover", onDragOver)

    // 2. ペースト (クリップボード画像)
    const onPaste = (e) => {
      const items = Array.from(e.clipboardData?.items || [])
      const fileItems = items.filter(i => i.kind === "file" && i.type.startsWith("image/"))
      if (fileItems.length === 0) return
      e.preventDefault()
      fileItems.forEach(item => {
        const f = item.getAsFile()
        if (f) this.uploadAndInsertImage(f)
      })
    }
    surface.addEventListener("paste", onPaste)

    this.cleanupImageDnd = () => {
      surface.removeEventListener("drop", onDrop)
      surface.removeEventListener("dragover", onDragOver)
      surface.removeEventListener("paste", onPaste)
    }
  }

  // ─── ツールバーレンダリング (ミニマル) ────────────────

  renderToolbar() {
    if (!this.hasToolbarTarget) return
    const tb = this.toolbarTarget
    tb.className = "akebono-toolbar sticky top-[52px] z-10 bg-akebono-bg border-b border-akebono-border px-2 py-1.5 flex items-center gap-1 overflow-x-auto"
    tb.innerHTML = ""

    // 1. ブロック種別ドロップダウン
    const blockBtn = this.makeDropdownButton({
      id: "block-type",
      label: "ブロック種別",
      contentHtml: `<span data-block-label class="text-sm">段落</span> ${lucideSvg(ChevronDown, "w-3 h-3 opacity-60")}`,
      groups: BLOCK_TYPE_GROUPS,
      extraClass: "akebono-tb-dropdown",
    })
    tb.appendChild(blockBtn)

    // sep
    tb.appendChild(this.makeSep())

    // 2. インライン装飾 (4)
    tb.appendChild(this.makeIconBtn({ name: "bold", action: "toggleBold",
      label: "太字", shortcut: `${Mod}+B`, icon: lucideSvg(Bold) }))
    tb.appendChild(this.makeIconBtn({ name: "italic", action: "toggleItalic",
      label: "斜体", shortcut: `${Mod}+I`, icon: lucideSvg(Italic) }))
    tb.appendChild(this.makeIconBtn({ name: "strikethrough", action: "toggleStrike",
      label: "取消線", icon: lucideSvg(Strikethrough) }))
    tb.appendChild(this.makeIconBtn({ name: "code", action: "toggleCode",
      label: "インラインコード", shortcut: `${Mod}+E`, icon: lucideSvg(Code) }))
    tb.appendChild(this.makeIconBtn({ name: "link", action: "insertLink",
      label: "リンク", shortcut: `${Mod}+K`, icon: lucideSvg(LinkIcon) }))
    tb.appendChild(this.makeIconBtn({ name: "image", action: "insertImage",
      label: "画像", icon: lucideSvg(ImageIcon) }))

    // sep
    tb.appendChild(this.makeSep())

    // 3. 挿入ドロップダウン
    const insertBtn = this.makeDropdownButton({
      id: "insert",
      label: "挿入",
      contentHtml: `${lucideSvg(Plus, "w-4 h-4")} <span class="text-sm">挿入</span> ${lucideSvg(ChevronDown, "w-3 h-3 opacity-60")}`,
      groups: INSERT_GROUPS,
      extraClass: "akebono-tb-dropdown",
    })
    tb.appendChild(insertBtn)


    // 残りの空間
    const flex = document.createElement("span")
    flex.className = "flex-1"
    tb.appendChild(flex)

    // 4. Undo / Redo
    tb.appendChild(this.makeIconBtn({ name: "undo", action: "undo",
      label: "元に戻す", shortcut: `${Mod}+Z`, icon: lucideSvg(Undo2) }))
    tb.appendChild(this.makeIconBtn({ name: "redo", action: "redo",
      label: "やり直し", shortcut: `${Mod}+⇧+Z`, icon: lucideSvg(Redo2) }))
  }

  makeIconBtn({ name, action, label, shortcut, icon }) {
    const btn = document.createElement("button")
    btn.type = "button"
    btn.tabIndex = -1  // keyboard focus 不可 → click でも focus が surface から移動しない
    btn.className = "akebono-tb-btn"
    btn.dataset.tbName = name
    btn.dataset.action = `editor#${action}`
    btn.setAttribute("title", `${label}${shortcut ? ` (${shortcut})` : ""}`)
    btn.setAttribute("aria-label", label)
    btn.innerHTML = icon
    // mousedown を preventDefault してエディタの selection を保持する
    // (これが無いと「テキスト選択 → B クリック」 で selection が collapse して
    //  FORMAT_TEXT_COMMAND が何も格納しない)
    btn.addEventListener("mousedown", (e) => e.preventDefault())
    return btn
  }

  makeSep() {
    const sep = document.createElement("span")
    sep.className = "akebono-tb-sep"
    return sep
  }

  // ドロップダウン (tippy.js)
  makeDropdownButton({ id, label, contentHtml, groups, extraClass }) {
    const btn = document.createElement("button")
    btn.type = "button"
    btn.tabIndex = -1
    btn.className = `akebono-tb-btn ${extraClass || ""} flex items-center gap-1.5 px-2`
    btn.setAttribute("title", label)
    btn.dataset.tbDropdown = id
    btn.innerHTML = contentHtml
    // dropdown を開く前にエディタ selection を保持
    btn.addEventListener("mousedown", (e) => e.preventDefault())

    // ドロップダウン content
    const menu = document.createElement("div")
    menu.className = "akebono-menu-popup bg-akebono-surface border border-akebono-border rounded-lg shadow-lg overflow-y-auto py-1 w-64 max-h-96"
    groups.forEach((group, gi) => {
      if (gi > 0) {
        const sep = document.createElement("div")
        sep.className = "border-t border-akebono-border my-1"
        menu.appendChild(sep)
      }
      if (group.label) {
        const lab = document.createElement("div")
        lab.className = "px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-akebono-text-subtle"
        lab.textContent = group.label
        menu.appendChild(lab)
      }
      group.items.forEach(item => {
        const opt = document.createElement("button")
        opt.type = "button"
        opt.className = "w-full text-left px-3 py-1.5 flex items-center gap-3 hover:bg-akebono-hover"
        opt.innerHTML = `
          <span class="w-6 h-6 inline-flex items-center justify-center rounded bg-akebono-hover text-akebono-text-muted shrink-0 font-mono text-xs">${item.icon}</span>
          <span class="flex-1 min-w-0">
            <span class="block text-sm text-akebono-text">${item.title}</span>
            <span class="block text-[11px] text-akebono-text-muted truncate">${item.desc}</span>
          </span>
        `
        opt.addEventListener("mousedown", (e) => {
          e.preventDefault()
          item.run(this.editor)
          popup.hide()
        })
        menu.appendChild(opt)
      })
    })

    const popup = tippy(btn, {
      content: menu,
      interactive: true,
      trigger: "click",
      placement: "bottom-start",
      theme: "akebono",
      arrow: false,
      offset: [0, 6],
      // toolbar が overflow-x-auto を持つせいで CSS 仕様により overflow-y も
      // visible でなくなり、 popup が toolbar 領域内で clip されてしまう。
      // body に append して viewport 直下に mount することで回避する。
      appendTo: () => document.body,
    })

    this.dropdownPopups = this.dropdownPopups || []
    this.dropdownPopups.push(popup)
    return btn
  }

  // ─── アクティブ状態 & block label の更新 ────────────────

  refreshToolbarState() {
    if (!this.hasToolbarTarget) return
    this.editor.read(() => {
      const sel = $getSelection()
      let state = {}
      let blockLabel = "段落"

      if ($isRangeSelection(sel)) {
        state = {
          bold:          sel.hasFormat("bold"),
          italic:        sel.hasFormat("italic"),
          strikethrough: sel.hasFormat("strikethrough"),
          code:          sel.hasFormat("code"),
        }
        const anchorNode = sel.anchor.getNode()
        const block = anchorNode.getKey() === "root" ? anchorNode : anchorNode.getTopLevelElementOrThrow()

        if ($isHeadingNode(block)) {
          const tag = block.getTag()
          blockLabel = { h1: "見出し 1", h2: "見出し 2", h3: "見出し 3", h4: "見出し 4" }[tag] || tag
        } else if ($isQuoteNode(block))    { blockLabel = "引用" }
        else if ($isCodeNode(block))       { blockLabel = "コード" }
        else if (block.getType() === "paragraph") { blockLabel = "段落" }
        else if ($isListNode(block) || $isListItemNode(anchorNode.getParent()))   { blockLabel = "リスト" }

        // リンク
        const nodes = sel.getNodes()
        state.link = nodes.some(n => $isLinkNode(n.getParent()) || $isLinkNode(n))

        // テーブル内かどうか (table dropdown のハイライト用)
        const inTable = nodes.some(n => $isTableCellNode($getTableCellNodeFromLexicalNode(n)))
        state.table = inTable
      }

      this.toolbarTarget.querySelectorAll("[data-tb-name]").forEach(btn => {
        btn.classList.toggle("is-active", !!state[btn.dataset.tbName])
      })
      const lbl = this.toolbarTarget.querySelector("[data-block-label]")
      if (lbl) lbl.textContent = blockLabel
    })
  }

  // ─── アクション ─────────────────────────────────────

  toggleBold()   { this.editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold") }
  toggleItalic() { this.editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic") }
  toggleStrike() { this.editor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough") }
  toggleCode()   { this.editor.dispatchCommand(FORMAT_TEXT_COMMAND, "code") }

  insertLink(event) {
    event?.preventDefault()
    const url = window.prompt("URL (http://, https://, mailto:)", "")
    if (url === null) return
    this.editor.dispatchCommand(TOGGLE_LINK_COMMAND, url === "" ? null : url)
  }

  insertImage(event) {
    event?.preventDefault()
    this.pickAndInsertImage()
  }

  undo() { this.editor.dispatchCommand(UNDO_COMMAND, undefined) }
  redo() { this.editor.dispatchCommand(REDO_COMMAND, undefined) }

  // ─── 差分プレビュー ──────────────────────────────────
  async showDiff(event) {
    event?.preventDefault()
    if (!this.hasDiffModalTarget) return
    const btn = event?.currentTarget
    const url = btn?.dataset.editorDiffUrl
    if (!url) return

    // modal を即座に表示 (ローディング状態)
    this.diffModalTarget.classList.remove("hidden")
    this.diffBodyTarget.innerHTML = '<p class="text-center py-8 text-akebono-text-muted text-sm">差分を読み込み中…</p>'

    try {
      const html = this.textareaTarget?.value || ""
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content || ""
      const form = new FormData()
      form.append("html", html)
      const res = await fetch(url, {
        method: "POST",
        headers: { "X-CSRF-Token": csrf, "Accept": "text/html" },
        body: form,
        credentials: "same-origin",
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const text = await res.text()
      this.diffBodyTarget.innerHTML = text
    } catch (err) {
      console.error("[editor] diff fetch failed", err)
      this.diffBodyTarget.innerHTML =
        `<p class="text-center py-8 text-akebono-danger text-sm">差分の取得に失敗しました: ${err.message}</p>`
    }
  }

  hideDiff(event) {
    event?.preventDefault()
    if (this.hasDiffModalTarget) this.diffModalTarget.classList.add("hidden")
  }
}
